/**
 * Shared pieces of the read-only notification content pages (metaplan A10; AIE §10 "Read-only views"):
 * blocks, block lists, themes, language policies and aliases. Backend: read-only `/v2/edit/` viewsets
 * (`../apiv2/dashboard_ai_edit.openapi.yaml`, tag `content`); pack / system rows come along, marked
 * `is_system`, and a system row the operator shadows with its own codename is `overridden`.
 */
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import { getContent } from '../aiChanges/api';

export const OwnerChips = ({ row }) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {row.is_system ? (
            <Chip size="small" variant="outlined" label="System" title="Shared by every operator that has not overridden it" />
        ) : (
            <Chip size="small" variant="outlined" color="primary" label={row.tour_operator?.name || 'Yours'} />
        )}
        {row.overridden && (
            <Chip size="small" variant="outlined" color="warning" label="Overridden" title="Your own row with this codename is the one used" />
        )}
        {row.enabled === false && <Chip size="small" label="Disabled" />}
    </Stack>
);

/** Read-only monospace text (definitions, shells): never an editor. */
export const CodeView = ({ label, value, emptyText = '—' }) => (
    <Box sx={{ mb: 2 }}>
        {label && (
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
        )}
        <Box
            component="pre"
            data-testid={label ? `code-${label}` : undefined}
            sx={{
                m: 0,
                p: 1.5,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 13,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 420,
                overflow: 'auto',
            }}
        >
            {value === undefined || value === null || value === '' ? emptyText : typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
        </Box>
    </Box>
);

export const Field = ({ label, children }) => (
    <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
            {label}
        </Typography>
        <Typography variant="body2" component="div">
            {children === undefined || children === null || children === '' ? '—' : children}
        </Typography>
    </Box>
);

export const RowLink = ({ to, children }) => (
    <RouterLink to={to} style={{ color: 'inherit' }}>
        {children}
    </RouterLink>
);

/** Loads one row of `resource` and renders `render(row)`; 404 / errors as an alert. */
export const ContentDetailLoader = ({ resource, id, render, backTo, backLabel }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [state, setState] = React.useState({ loading: true, row: null, error: null });
    React.useEffect(() => {
        let alive = true;
        setState({ loading: true, row: null, error: null });
        getContent(dataAPI, resource, id)
            .then((row) => alive && setState({ loading: false, row, error: null }))
            .catch((error) => alive && setState({ loading: false, row: null, error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, resource, id]);
    return (
        <Box p={2}>
            {backTo && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <RowLink to={backTo}>← {backLabel || 'Back'}</RowLink>
                </Typography>
            )}
            {state.loading && <CircularProgress size={24} />}
            {state.error && (
                <Alert severity={state.error.status === 404 ? 'warning' : 'error'}>
                    {state.error.status === 404 ? 'Not found (or not visible to you).' : `Could not load it: ${state.error.message}`}
                </Alert>
            )}
            {state.row && render(state.row)}
        </Box>
    );
};
